import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { resetPassword } from '../actions/authActions';
import Message from '../components/ui/Message';
import Loader from '../components/ui/Loader';
import VerificationCodeInput from '../components/ui/VerificationCodeInput';

const PasswordResetPage = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('error'); // 'error' or 'success'
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Enter phone, 2: Enter code and new password
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const handleNextStep = () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      setMessage('请输入有效的手机号码');
      setMessageType('error');
      return;
    }
    
    setStep(2);
  };
  
  const handleVerificationChange = (code) => {
    setVerificationCode(code);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate inputs
    if (newPassword !== confirmPassword) {
      setMessage('两次输入的密码不一致');
      setMessageType('error');
      return;
    }
    
    if (!verificationCode || verificationCode.length !== 6) {
      setMessage('请输入6位数字验证码');
      setMessageType('error');
      return;
    }
    
    if (!newPassword || newPassword.length < 6) {
      setMessage('密码必须至少有6个字符');
      setMessageType('error');
      return;
    }
    
    setMessage(null);
    setLoading(true);
    
    try {
      const result = await dispatch(resetPassword(phoneNumber, verificationCode, newPassword));
      
      if (result.success) {
        setMessage(result.message || '密码重置成功，请使用新密码登录');
        setMessageType('success');
        
        // Redirect to login page after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setMessage(result.message);
        setMessageType('error');
      }
    } catch (error) {
      setMessage('密码重置失败，请稍后再试');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="flex justify-center">
      <div className="w-full max-w-md">
        <h1 className="text-3xl font-bold mb-6 text-center">重置密码</h1>
        
        {message && (
          <Message 
            variant={messageType} 
            onClose={() => setMessage(null)}
          >
            {message}
          </Message>
        )}
        
        <div className="bg-white p-6 rounded shadow">
          {step === 1 ? (
            // Step 1: Enter phone number
            <div>
              <p className="mb-4 text-gray-600">
                请输入您的手机号码，我们将发送验证码用于重置密码。
              </p>
              
              <div className="mb-4">
                <label className="block text-gray-700 mb-1">手机号码</label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="请输入手机号码"
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              
              <button
                type="button"
                onClick={handleNextStep}
                className="w-full bg-red-700 text-white py-2 rounded hover:bg-red-800"
              >
                下一步
              </button>
              
              <div className="mt-4 text-center">
                <Link to="/login" className="text-red-700 hover:underline">
                  返回登录
                </Link>
              </div>
            </div>
          ) : (
            // Step 2: Enter verification code and new password
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <VerificationCodeInput
                  phoneNumber={phoneNumber}
                  onVerificationChange={handleVerificationChange}
                  verificationType="password_reset"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-700 mb-1">新密码</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="请输入新密码"
                  className="w-full p-2 border rounded"
                  required
                  minLength={6}
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 mb-1">确认新密码</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="请确认新密码"
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              
              <button
                type="submit"
                className="w-full bg-red-700 text-white py-2 rounded hover:bg-red-800"
                disabled={loading}
              >
                {loading ? <Loader size="sm" /> : '重置密码'}
              </button>
              
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-gray-600 hover:underline"
                >
                  返回上一步
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordResetPage;